import React from "react";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Placement } from "react-bootstrap/esm/types";
import { OverlayTriggerType } from "react-bootstrap/esm/OverlayTrigger";
import styled from "styled-components";
import i18n from "i18next";
import { shallowEqual } from "react-redux";

import { dynamicTextColor, getAcronym } from "../../utils/helpers";
import { useAppSelector } from "../../redux/hooks";
import { configAgencyName, statuteMile } from "../../config";
import variableColors from "../../_App.module.scss";
import busBlue from "../../assets/icons/busBlue.svg";

const { darkColor, primaryColor } = variableColors;

//* Asterisk for Forms
const AsteriskSpan = styled.span<{ $color: string }>`
  color: ${(props) => props?.$color || "inherit"};
  font-weight: bold;
`;

//* NoData info as styled span: Bold and Maroon used below in TripProgress component
export const BoldMaroonNodata = (): JSX.Element => {
  const { t } = useTranslation();

  return <span className="span_bold span_maroon">{t("b/d")}</span>;
};

const InfoKms = ({ currentBus }: { currentBus: BusInfo }): JSX.Element => {
  const { distanceAlongTrip, tripLength } = currentBus || {};
  const leftDistanceKms = Number(((tripLength - distanceAlongTrip) / 1000).toFixed(1));

  return (
    <React.Fragment>
      <span className="span_bold span_maroon">{`${currentBus?.currentProgress}%`}</span>
      <span>{" ( "}</span>
      <span className="span_bold span_maroon">{`${(0.001 * currentBus?.distanceAlongTrip).toFixed(1)}`}</span>
      <span>{" / "}</span>
      <span className="span_bold span_maroon">{`${(0.001 * currentBus?.tripLength).toFixed(1)} km`}</span>
      <span>{" ) ("}</span>
      <span className="span_bold span_maroon">{`${leftDistanceKms} km`}</span>
      <span>{" )"}</span>
    </React.Fragment>
  );
};

const InfoMiles = ({ currentBus }: { currentBus: BusInfo }): JSX.Element => {
  const { distanceAlongTrip, tripLength } = currentBus || {};
  const leftDistanceMiles = Number(((tripLength - distanceAlongTrip) / (1000 * statuteMile)).toFixed(1));

  return (
    <React.Fragment>
      <span className="span_bold span_maroon">{`${currentBus?.currentProgress}%`}</span>
      <span>{" ( "}</span>
      <span className="span_bold span_maroon">{`${((0.001 * currentBus?.distanceAlongTrip) / statuteMile).toFixed(
        1
      )}`}</span>
      <span>{" / "}</span>
      <span className="span_bold span_maroon">{`${((0.001 * currentBus?.tripLength) / statuteMile).toFixed(1)} miles`}</span>
      <span>{" ) ("}</span>
      <span className="span_bold span_maroon">{`${leftDistanceMiles} miles`}</span>
      <span>{" )"}</span>
    </React.Fragment>
  );
};

//* Bus progress by bus
export const TripProgress = ({ currentBus, speedUnit }: { currentBus: BusInfo; speedUnit: string }): JSX.Element => {
  return (
    <React.Fragment>
      {currentBus?.currentProgress || currentBus?.currentProgress === 0 ? (
        <React.Fragment>
          {speedUnit === "kmh" ? (
            <InfoKms currentBus={currentBus} />
          ) : speedUnit === "mph" ? (
            <InfoMiles currentBus={currentBus} />
          ) : (
            <BoldMaroonNodata />
          )}
        </React.Fragment>
      ) : (
        <BoldMaroonNodata />
      )}
    </React.Fragment>
  );
};

//* Reusable Tooltip
export const ReusableTooltip = ({
  id,
  children,
  title,
  text,
  placement = "right",
  trigger = ["hover", "focus"],
}: {
  id: string;
  children: JSX.Element;
  title?: string | null;
  text: string | JSX.Element;
  placement?: Placement;
  trigger?: OverlayTriggerType[];
}) => {
  return (
    <React.Fragment>
      <OverlayTrigger
        trigger={trigger}
        placement={placement}
        overlay={
          <Tooltip id={id}>
            {title ? <h6>{title}</h6> : null}
            {text}
          </Tooltip>
        }
      >
        {children}
      </OverlayTrigger>
    </React.Fragment>
  );
};

//* FormAsterisk
export const FormAsterisk = ({ color }: { color: string }): JSX.Element => {
  return <AsteriskSpan $color={color}>*</AsteriskSpan>;
};

//* AllNetworkPlaceholder
export const BadgeAllNetworkPlaceholder = ({
  color,
  agencyName,
  agencyId,
}: {
  color: string;
  agencyName: string;
  agencyId: string;
}): JSX.Element => (
  <span
    className="badge rounded-pill fw-bold"
    style={{ fontSize: "100%", background: color, color: dynamicTextColor(color) }}
  >
    {`${i18n.t("All_Network")} - ${agencyName} (${agencyId})`}
  </span>
);

//* Available Routes for a Stop
const AvailableRouteDivContainer = styled.div`
  p {
    margin-top: 0.5rem;
    margin-bottom: 0.25rem;
    color: ${darkColor};
  }
  div.routes-container {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    align-content: center;
    flex-wrap: wrap;
    span {
      &:hover {
        font-style: italic;
      }
    }
  }
`;

//* Available Routes for a Stop
export const StopAvailableRoutes: React.FC<{ stopId: string; showHeader?: boolean }> = ({
  stopId,
  showHeader = true,
}: {
  stopId: string;
  showHeader?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const [routesInfo]: [RouteDetails[]] = useAppSelector((state: RootState) => [state?.routesInfo?.routesInfo], shallowEqual);

  const [stopAvailableRoutes, setStopAvailableRoutes] = React.useState<AvailableRoute[]>([]);

  //* Find routes via selectedStop
  React.useEffect((): void => {
    const routesViaStop: AvailableRoute[] = routesInfo
      ?.filter((elem: RouteDetails) => elem?.routeStopsIds?.includes(stopId))
      ?.map((elem: RouteDetails): AvailableRoute => {
        return {
          id: elem?.id,
          backgroundColor: elem?.color,
          longName: elem?.longName,
          shortName: elem?.shortName,
          color: dynamicTextColor(elem?.color),
          acronym: configAgencyName === ("MiamiBeach" as string) ? getAcronym(elem?.shortName) : "", //* Add Acronym if agency is Miami Beach
        };
      });
    // console.log("routesViaStop:", routesViaStop);
    setStopAvailableRoutes(routesViaStop);
  }, [routesInfo, stopId]);

  const availableRoutes: JSX.Element[] = stopAvailableRoutes?.map((stopElem: AvailableRoute, index: number): JSX.Element => {
    return (
      <ReusableTooltip
        key={`${index}_${stopElem?.id}`}
        placement="auto"
        id={`Tooltip_stop_${index}_${stopElem?.id}`}
        children={
          <span
            key={`${index}_${stopElem?.id}`}
            style={{
              color: stopElem?.color,
              backgroundColor: stopElem?.backgroundColor,
              display: "inline-block",
              textAlign: "center",
              minWidth: "4rem",
              padding: "1px 3px",
              border: `1px dotted ${darkColor}`,
              fontSize: "0.8rem",
              fontWeight: "bold",
              textDecoration: "underline",
              textDecorationStyle: "dotted",
              cursor: "pointer",
            }}
          >
            {stopElem?.acronym || stopElem?.shortName || stopElem?.id}
          </span>
        }
        text={stopElem?.longName || stopElem?.id}
      />
    );
  });

  return (
    <React.Fragment>
      {availableRoutes ? (
        <AvailableRouteDivContainer>
          {showHeader ? (
            <p>
              {t("Dostępne linie")} <span className="span_bold span_maroon">{`(${availableRoutes?.length})`}</span>
            </p>
          ) : null}
          <div className="routes-container">{availableRoutes}</div>
        </AvailableRouteDivContainer>
      ) : null}
    </React.Fragment>
  );
};

export const StyledStatsSpan = ({
  text,
  value,
  addon,
}: {
  text: string;
  value: string | number;
  addon?: string;
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <React.Fragment>
      <span>
        {t(text)}:{" "}
        <span className="span_bold">
          {value} {addon}
        </span>
      </span>
    </React.Fragment>
  );
};

export const NoDownloadableContent = ({ str }: { str: string }): JSX.Element => {
  const { t } = useTranslation();

  return (
    <React.Fragment>
      <h3 className="text-center span_bold m-5">{t(str)}</h3>
    </React.Fragment>
  );
};

//* DatePicker - CalendarIconSVG
export const CalendarIconSVG = (): JSX.Element => {
  return (
    <React.Fragment>
      <svg
        version="1.1"
        height="20"
        viewBox="0 0 24 24"
        width="20"
        xmlns="http://www.w3.org/2000/svg"
        fill={primaryColor}
        style={{ transform: "translateY(-1px)", marginRight: "0.25rem" }}
      >
        <path d="M5 22h14c1.103 0 2-.897 2-2V6c0-1.103-.897-2-2-2h-2V2h-2v2H9V2H7v2H5c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2zm6-3.586-3.707-3.707 1.414-1.414L11 15.586l4.293-4.293 1.414 1.414L11 18.414zM5 7h14v2H5V7z" />
      </svg>
    </React.Fragment>
  );
};

//* Number of buses on route/ direction
export const RenderBusesQuantity = ({ quantity }: { quantity: number }): JSX.Element => {
  if (!quantity) {
    return (
      <React.Fragment>
        {String.fromCharCode(8658)} <span className="span_bold">({quantity})</span>
      </React.Fragment>
    );
  }

  const arr: unknown[] = Array.from({ length: quantity });

  return (
    <React.Fragment>
      {String.fromCharCode(8658)}{" "}
      {arr.map(
        (_: unknown, index: number): JSX.Element => (
          <img alt="bus icon" src={busBlue} key={index} width={22} height={22} style={{ transform: "translateY(-1px)" }} />
        )
      )}{" "}
      <span className="span_bold">({quantity})</span>
    </React.Fragment>
  );
};

//* Link from TableBigComponent to
export const ToAccordionWithState = ({ routeColor }: { routeColor: string }): JSX.Element => {
  return (
    <React.Fragment>
      <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" width={14} height={14}>
        <path
          d="M7.05025 1.53553C8.03344 0.552348 9.36692 0 10.7574 0C13.6528 0 16 2.34721 16 5.24264C16 6.63308 15.4477 7.96656 14.4645 8.94975L12.4142 11L11 9.58579L13.0503 7.53553C13.6584 6.92742 14 6.10264 14 5.24264C14 3.45178 12.5482 2 10.7574 2C9.89736 2 9.07258 2.34163 8.46447 2.94975L6.41421 5L5 3.58579L7.05025 1.53553Z"
          fill={routeColor}
        />
        <path
          d="M7.53553 13.0503L9.58579 11L11 12.4142L8.94975 14.4645C7.96656 15.4477 6.63308 16 5.24264 16C2.34721 16 0 13.6528 0 10.7574C0 9.36693 0.552347 8.03344 1.53553 7.05025L3.58579 5L5 6.41421L2.94975 8.46447C2.34163 9.07258 2 9.89736 2 10.7574C2 12.5482 3.45178 14 5.24264 14C6.10264 14 6.92742 13.6584 7.53553 13.0503Z"
          fill={routeColor}
        />
        <path d="M5.70711 11.7071L11.7071 5.70711L10.2929 4.29289L4.29289 10.2929L5.70711 11.7071Z" fill={routeColor} />
      </svg>
    </React.Fragment>
  );
};
